import _, {chain} from 'lodash';
import {createSelector} from 'reselect';

import {
  findSignature,
  selectSignaturesForChannel,
  selectSignaturesForNamespace,
} from '../../../helpers/models/signatureHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {DraftMessageModel} from '../../../models/api/messageModel';
import {AppState} from '../../appState';
import {AsyncStatus} from '../../appTypes';
import {getOptimisticData} from '../dataSelectors';
import {getUserNamespace} from '../teammates/teammatesSelectors';

/*
 * Signatures.
 */

/** Get a map of all signatures by id. */
export function getSignatures(state: AppState) {
  return state.data.signatures.byId;
}

export function getSignaturesAsyncStatus(state: AppState): AsyncStatus {
  return state.data.signatures;
}

export function getSignatureById(state: AppState, signatureId: number) {
  return state.data.signatures.byId[signatureId];
}

export const getSignatureIds = createSelector(getSignatures, (signatures) =>
  chain(signatures)
    .keys()
    .map((signatureId) => Number(signatureId))
    .value(),
);

/** Get a flat list of all signatures. */
export const getFlatSignatures = createSelector(getSignatures, (signatures) =>
  chain(signatures).values().compact().value(),
);

/** Get a sorted flat list of all signatures. */
export const getSortedSignatures = createSelector(
  [getOptimisticData, getUserNamespace],
  selectSignaturesForNamespace,
);

/** Find the available signatures for the specified channel. */
export function makeGetAvailableSignatures<P>(getChannel: AppParametricSelector<P, ChannelModel>) {
  return createSelector([getOptimisticData, getChannel], selectSignaturesForChannel);
}

/** Find the currently selected signature. */
export function makeGetSelectedSignature<P>(
  getChannel: AppParametricSelector<P, ChannelModel>,
  getMessage: AppParametricSelector<P, DraftMessageModel>,
) {
  const getAvailableSignatures = makeGetAvailableSignatures(getChannel);

  return createSelector([getMessage, getAvailableSignatures], (message, availableSignatures) => {
    const {signatureId} = message;
    return findSignature(signatureId, availableSignatures);
  });
}

export const getSignaturesByNamespace = createSelector(getFlatSignatures, (signatures) =>
  _(signatures)
    .groupBy((s) => s.namespace)
    .value(),
);

export const getUserOwnedSignatures = createSelector(
  [getSignaturesByNamespace, getUserNamespace],
  (signatures, userNamespace) => signatures[userNamespace],
);

export const getUserOwnedSignaturesCount = createSelector(
  getUserOwnedSignatures,
  (signatures) => signatures?.length || 0,
);
